import { useEffect, useState, useCallback } from "react";
import { AppConfig } from "../config";

const { breakpoints, breakpointsExtension } = AppConfig.responsive;
const breakpointsNames = breakpoints.map(({ name }) => name);

type ElementType<T extends ReadonlyArray<unknown>> = T extends ReadonlyArray<
  infer ElementType
>
  ? ElementType
  : never;

type PageSizes = ElementType<typeof breakpointsNames>;

// ascend
const breakpointsA = breakpoints.sort((a, b) => {
  if (a.breakPoint > b.breakPoint) return 1;
  if (a.breakPoint < b.breakPoint) return -1;
  return 0;
});

type Value = {
  [k in PageSizes]: boolean;
};
const emptyValue: Value = breakpointsA.reduce(
  (a, { name }) => ({ ...a, [name]: false }),
  {} as any
);
const filledValue: Value = breakpointsA.reduce(
  (a, { name }) => ({ ...a, [name]: true }),
  {} as any
);

export const usePageSize = (): Value => {
  const handle = useCallback((): Value => {
    const w =
      breakpointsExtension === "em"
        ? window.innerWidth /
          parseFloat(window.getComputedStyle(document.body).fontSize)
        : window.innerWidth;

    const val = { ...emptyValue };

    if (w < breakpointsA[0].breakPoint) {
      return {
        ...emptyValue,
        [breakpointsA[0].name]: true,
      };
    }
    if (w > breakpointsA[breakpointsA.length - 1].breakPoint) {
      return filledValue;
    }

    breakpointsA.forEach((i) => {
      val[i.name] = w > i.breakPoint;
    });
    return val;
  }, []);

  const eventListener = useCallback(
    (set: (v: Value) => void) => set(handle()),
    [handle]
  );

  const [size, setSize] = useState<Value>(handle());

  useEffect(() => {
    window.addEventListener("resize", () => eventListener(setSize));
    return () => {
      window.removeEventListener("resize", () => eventListener(setSize));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return size;
};
